import { BranchSharedUrlDataSchema } from 'api/branch/types';
import type { DeepLinkData } from 'branch-sdk';
import { useBranch } from 'lib/branch/BranchProvider';
import { useCallback, useRef, useState } from 'react';

type CreateFnReturn = Promise<{
  link?: string | null;
  branchLinkDetails?: DeepLinkData;
  error?: string;
  shareLinkId?: string;
}>;

const useGenerateBranchLink = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null | undefined>();
  const [link, setLink] = useState<string | null | undefined>();
  const [branchDeepLinkData, setBranchDeepLinkData] =
    useState<branch.DeepLinkData>();
  // Prevents race conditions: only the latest create() request may update this hook instance's state.
  const latestRequestIdRef = useRef(0);
  const { generateBranchUrl } = useBranch();

  const create = useCallback(
    async (data: BranchSharedUrlDataSchema): CreateFnReturn => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      return new Promise(resolve => {
        try {
          setError(null);
          setLoading(true);

          generateBranchUrl({
            data,
            cb: ({ error, link, branchLinkDetails }) => {
              const isLatestRequest = requestId === latestRequestIdRef.current;

              if (error) {
                if (isLatestRequest) {
                  setLink(null);
                  setBranchDeepLinkData(undefined);
                  setError(error);
                }
                resolve({ error });
              } else {
                if (isLatestRequest) {
                  setError(null);
                  setLink(link);
                  setBranchDeepLinkData(branchLinkDetails);
                }
                resolve({
                  link,
                  branchLinkDetails,
                  shareLinkId: branchLinkDetails.data?.$share_link_id
                });
              }

              if (isLatestRequest) {
                setLoading(false);
              }
            }
          });
        } catch (error) {
          const err = error as Error;

          if (requestId === latestRequestIdRef.current) {
            setError(err?.message);
            setLoading(false);
          }
          resolve({ error: err?.message });
        }
      });
    },
    [generateBranchUrl]
  );

  return {
    create,
    link,
    shareLinkId: branchDeepLinkData?.data?.$share_link_id as string | undefined,
    branchDeepLinkData,
    loading,
    error
  };
};

export default useGenerateBranchLink;
